import React, { useEffect, useRef, useState } from 'react';
import SlideShell from '../SlideShell';

const SCROLLBAR_HIDE_CLASS = 'mm-scrollbar-hide';
const VIDEO_WIDTH = 'clamp(170px, 55vw, 240px)';
const VIDEO_ASPECT_RATIO = '9 / 16';
const VIDEO_CORNER_RADIUS = '10px';
const VIDEO_CARD_TOP_PADDING = '1rem';
const VIDEO_SECTION_TOP_PADDING = '1.5rem';
const FOCUS_CONFIRM_MS = 500;

let hasInjectedScrollbarStyles = false;
function ensureScrollbarStyles() {
  if (hasInjectedScrollbarStyles || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.type = 'text/css';
  style.textContent = `
    .${SCROLLBAR_HIDE_CLASS}::-webkit-scrollbar { display: none; height: 0; width: 0; }
    .${SCROLLBAR_HIDE_CLASS} { scrollbar-width: none !important; -ms-overflow-style: none !important; }
  `;
  document.head.appendChild(style);
  hasInjectedScrollbarStyles = true;
}

// Local path utilities (replacing Firebase storage listing)
function isMp4Path(path) {
  return /\.mp4$/i.test(path || '');
}

// Build local video paths from a folder code (we keep limit 8, though current folders contain ~5 files)
const LOCAL_TIKTOK_MAX = 8;
const DEFAULT_LOCAL_INDICES = [1, 2, 3, 4, 5]; // fallback indices present per requirement

function buildLocalPaths(storagePrefix) {
  // storagePrefix format: 'tiktok/W' etc. Public root has /media/tiktok/<code>/
  const indices = DEFAULT_LOCAL_INDICES.slice(0, LOCAL_TIKTOK_MAX);
  return indices.map((i) => `/media/${storagePrefix}/${i}-web-24fps.mp4`);
}

function derivePosterFromMp4(mp4Path) {
  // mp4 pattern: /media/tiktok/<code>/<n>-web-24fps.mp4 -> poster: /media/tiktok/<code>/<n>.webp
  if (!mp4Path) return null;
  const m = mp4Path.match(/\/(\d+)-web-24fps\.mp4$/);
  if (!m) return null;
  const number = m[1];
  return mp4Path.replace(/\/(\d+)-web-24fps\.mp4$/, `/${number}.webp`);
}

// iOS detection helper for thumbnail priming
const IS_IOS = typeof navigator !== 'undefined' && (
  /iP(hone|od|ad)/.test(navigator.platform || '') ||
  // iPadOS 13+ reports Mac, detect touch support
  (/Mac/.test(navigator.platform || '') && typeof document !== 'undefined' && 'ontouchend' in document)
);

function primeIOSThumbnail(videoEl) {
  if (!IS_IOS || !videoEl) return;
  try {
    videoEl.muted = true;
    videoEl.setAttribute('muted', '');
    videoEl.playsInline = true;
    videoEl.setAttribute('playsinline', '');
    videoEl.setAttribute('webkit-playsinline', '');
    // Nudge currentTime to force a frame paint if at 0
    if (videoEl.readyState >= 2 && videoEl.currentTime === 0) {
      try { videoEl.currentTime = 0.001; } catch {}
    }
    const p = videoEl.play?.();
    if (p && typeof p.then === 'function') {
      p.then(() => { try { videoEl.pause?.(); } catch {} }).catch(() => {});
    } else {
      try { videoEl.pause?.(); } catch {}
    }
  } catch {}
}

export default function TikTokEmbedSlide({
  title = '100+ hair loss winners similar to you.',
  subtitle = '87% of similar patients are satisfied with results. With the right doctor, you can too.',
  currentStep,
  totalSteps,
  onNext,
  onBack,
  nextLabel = 'Continue',
  theme = 'light',
  animationDirection = 'forward',
  isTransitioning = false,
  storagePrefix = 'tiktok/B',
  videoStoragePaths = null,
}) {
  const [paths, setPaths] = useState([]);
  const [isListing] = useState(false); // listing disabled (instant local paths)
  const [listingError] = useState(null);

  // Build local paths (or use provided override) once storagePrefix / overrides change
  useEffect(() => {
    if (Array.isArray(videoStoragePaths) && videoStoragePaths.length > 0) {
      const normalized = videoStoragePaths.filter(Boolean).filter((p) => isMp4Path(p)).slice(0, LOCAL_TIKTOK_MAX);
      setPaths(normalized);
      return;
    }
    setPaths(buildLocalPaths(storagePrefix));
  }, [storagePrefix, videoStoragePaths]);

  const [urls, setUrls] = useState([]);
  const [errors, setErrors] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0); // start from first
  const [needsUserPlay, setNeedsUserPlay] = useState(false);
  const [playingReady, setPlayingReady] = useState(false); // focused playing video ready
  const [posterUrls, setPosterUrls] = useState([]);

  // Refs used for scrolling and playback control
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const rafRef = useRef(0);
  const pendingCenterRef = useRef(0);
  const confirmTimerRef = useRef(0);

  useEffect(() => {
    setUrls(Array(paths.length).fill(null));
    setErrors(Array(paths.length).fill(null));
    setPosterUrls(Array(paths.length).fill(null));
    setCurrentIndex(0);
    setNeedsUserPlay(false);
    setPlayingReady(false);
  }, [paths]);

  // Resolve all posters up-front to keep thumbnails consistent across indices
  useEffect(() => {
    if (!paths.length) return;
    // Derive poster URLs synchronously from known filename convention
    const posters = paths.map((p) => derivePosterFromMp4(p));
    setPosterUrls(posters);
  }, [paths]);

  // Resolve current + neighbors videos only (to limit network/decoding)
  useEffect(() => {
    if (!paths.length) return;
    // For local assets, URLs are the direct paths; still maintain neighbor preloading style
    const nextUrls = [...urls];
    const setUrlIfEmpty = (i) => { if (i >= 0 && i < paths.length && !nextUrls[i]) nextUrls[i] = paths[i]; };
    setUrlIfEmpty(currentIndex);
    setUrlIfEmpty(currentIndex - 1);
    setUrlIfEmpty(currentIndex + 1);
    setUrls(nextUrls);
    setPlayingReady(false);
    setNeedsUserPlay(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex, paths.join('|')]);

  // Autoplay when URL is ready for focus; keep thumbnail visible until canplay
  useEffect(() => {
  const el = videoRef.current; const url = urls[currentIndex];
    if (!el || !url) return;

    if (el.getAttribute('src') !== url) {
      el.setAttribute('src', url);
      try { el.load(); } catch {}
      setPlayingReady(false);
    }

    const handleCanPlay = () => { setPlayingReady(true); };
    el.addEventListener('canplay', handleCanPlay, { once: true });

    const tryPlay = () => {
      try { const p = el.play?.(); if (p && typeof p.catch === 'function') p.catch(() => setNeedsUserPlay(true)); }
      catch { setNeedsUserPlay(true); }
    };

    const t = setTimeout(tryPlay, 40);
    return () => { clearTimeout(t); el.removeEventListener('canplay', handleCanPlay); };
  }, [urls, currentIndex]);

  useEffect(() => () => { try { videoRef.current?.pause?.(); } catch {}; if (rafRef.current) cancelAnimationFrame(rafRef.current); if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current); }, []);

  const handleUserPlay = () => { setNeedsUserPlay(false); try { videoRef.current?.play?.(); } catch { setNeedsUserPlay(true); } };

  const scrollToIndex = (idx) => {
    const container = containerRef.current; if (!container) return;
    const el = container.querySelector(`[data-index="${idx}"]`); if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  };

  const nearestReadyIndex = (targetIdx) => {
    if (urls[targetIdx]) return targetIdx;
    // Search outward for nearest ready
    let offset = 1;
    while (targetIdx - offset >= 0 || targetIdx + offset < urls.length) {
      const left = targetIdx - offset; const right = targetIdx + offset;
      if (left >= 0 && urls[left]) return left;
      if (right < urls.length && urls[right]) return right;
      offset += 1;
    }
    // Fallback to current if none ready
    return currentIndex;
  };

  const handlePrev = () => {
    if (currentIndex <= 0) return;
    const nextIdx = nearestReadyIndex(currentIndex - 1);
    if (nextIdx === currentIndex) return;
    try { videoRef.current?.pause?.(); } catch {}
    setCurrentIndex(nextIdx);
    scrollToIndex(nextIdx);
  };
  const handleNext = () => {
    if (currentIndex >= paths.length - 1) return;
    const nextIdx = nearestReadyIndex(currentIndex + 1);
    if (nextIdx === currentIndex) return;
    try { videoRef.current?.pause?.(); } catch {}
    setCurrentIndex(nextIdx);
    scrollToIndex(nextIdx);
  };

  const handleScroll = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => {
      const container = containerRef.current; if (!container) return;
      const center = container.scrollLeft + container.clientWidth / 2;
      const cards = container.querySelectorAll('[data-index]');
      let bestIdx = 0; let bestDist = Infinity;
      cards.forEach((el) => {
        const i = Number(el.getAttribute('data-index'));
        const left = el.offsetLeft; const width = el.clientWidth;
        const cardCenter = left + width / 2;
        const dist = Math.abs(cardCenter - center);
        if (dist < bestDist) { bestDist = dist; bestIdx = i; }
      });
      const readyIdx = nearestReadyIndex(bestIdx);
      if (pendingCenterRef.current !== readyIdx) {
        pendingCenterRef.current = readyIdx;
        if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
        confirmTimerRef.current = window.setTimeout(() => {
          if (pendingCenterRef.current !== currentIndex) {
            try { videoRef.current?.pause?.(); } catch {}
            setCurrentIndex(pendingCenterRef.current);
            scrollToIndex(pendingCenterRef.current);
          }
        }, FOCUS_CONFIRM_MS);
      }
    });
  };

  const prevDisabled = currentIndex <= 0;
  const nextDisabled = currentIndex >= paths.length - 1;

  return (
    <SlideShell
      title={title}
      subtitle={subtitle}
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={() => onNext?.({ tikTokVideosViewed: urls.filter(Boolean).length })}
      onBack={onBack}
      nextLabel={nextLabel}
      theme={theme}
      animationDirection={animationDirection}
      isTransitioning={isTransitioning}
    >
      <div className="space-y-6" style={{ paddingTop: VIDEO_SECTION_TOP_PADDING }}>
        {isListing && (
          <div className="flex justify-center">
            <div className="flex-shrink-0" style={{ width: VIDEO_WIDTH, paddingTop: VIDEO_CARD_TOP_PADDING }}>
              <div className="rounded-2xl bg-gray-200 animate-pulse" style={{ aspectRatio: VIDEO_ASPECT_RATIO, borderRadius: VIDEO_CORNER_RADIUS }} />
            </div>
          </div>
        )}

        {!isListing && listingError && paths.length === 0 && (
          <div className="flex items-center justify-center">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 max-w-sm text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Content unavailable</h3>
              <p className="text-sm text-gray-600">We could not load testimonials right now.</p>
            </div>
          </div>
        )}

        {!isListing && !listingError && paths.length === 0 && (
          <div className="flex items-center justify-center">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 max-w-sm text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">No content yet</h3>
              <p className="text-sm text-gray-600">We will add new testimonials shortly.</p>
            </div>
          </div>
        )}

        {paths.length > 0 && (
          <div className="relative">
            <div
              ref={containerRef}
              onScroll={handleScroll}
              className={[SCROLLBAR_HIDE_CLASS, 'flex overflow-x-auto snap-x snap-mandatory space-x-4 px-2 py-1'].join(' ')}
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              <div aria-hidden className="flex-shrink-0 pointer-events-none opacity-0" style={{ width: VIDEO_WIDTH, paddingTop: VIDEO_CARD_TOP_PADDING }} />

              {paths.map((_, index) => (
                <div key={`card-${index}`} data-index={index} className="flex-shrink-0" style={{ width: VIDEO_WIDTH, scrollSnapAlign: 'center', paddingTop: VIDEO_CARD_TOP_PADDING }}>
                  <div className="relative w-full" style={{ aspectRatio: VIDEO_ASPECT_RATIO, borderRadius: VIDEO_CORNER_RADIUS }}>
                    {index === currentIndex ? (
                      <>
                        {posterUrls[index] && (
                          <img
                            src={posterUrls[index]}
                            alt=""
                            className="absolute inset-0 h-full w-full object-cover"
                            style={{ borderRadius: VIDEO_CORNER_RADIUS }}
                          />
                        )}
                        {!posterUrls[index] && (
                          <div className="absolute inset-0 rounded-2xl bg-gray-200 animate-pulse" style={{ borderRadius: VIDEO_CORNER_RADIUS }} />
                        )}
                        <video
                          ref={videoRef}
                          src={urls[index] || ''}
                          className="h-full w-full object-cover"
                          style={{ borderRadius: VIDEO_CORNER_RADIUS, position: 'absolute', inset: 0, display: playingReady ? 'block' : 'none' }}
                          playsInline
                          webkit-playsinline=""
                          preload="metadata"
                          muted
                          autoPlay
                          loop
                          controls={false}
                          onLoadedData={() => setPlayingReady(true)}
                          onCanPlay={() => setPlayingReady(true)}
                        />
                      </>
                    ) : (
                      posterUrls[index] ? (
                        <img
                          src={posterUrls[index]}
                          alt=""
                          className="h-full w-full object-cover"
                          style={{ borderRadius: VIDEO_CORNER_RADIUS }}
                        />
                      ) : (
                        <div className="absolute inset-0 rounded-2xl bg-gray-200 animate-pulse" style={{ borderRadius: VIDEO_CORNER_RADIUS }} />
                      )
                    )}
                  </div>
                </div>
              ))}

              <div aria-hidden className="flex-shrink-0 pointer-events-none opacity-0" style={{ width: VIDEO_WIDTH, paddingTop: VIDEO_CARD_TOP_PADDING }} />
            </div>

            {/* Navigation arrows */}
            <div className="pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center justify-between px-1">
              <button
                type="button"
                onClick={handlePrev}
                disabled={prevDisabled}
                className="pointer-events-auto inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/80 shadow transition hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Previous video"
              >
                <svg className="h-5 w-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>

              <button
                type="button"
                onClick={handleNext}
                disabled={nextDisabled}
                className="pointer-events-auto inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/80 shadow transition hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Next video"
              >
                <svg className="h-5 w-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {paths.length > 0 && (
          <div className="flex justify-center space-x-2">
            {paths.map((_, i) => (
              <span
                key={`dot-${i}`}
                className={['h-2 w-2 rounded-full transition-all duration-300', i === currentIndex ? 'bg-gray-900' : urls[i] ? 'bg-gray-400' : 'bg-gray-200'].join(' ')}
              />
            ))}
          </div>
        )}
      </div>
    </SlideShell>
  );
}
